import * as _ from "lodash";
import { ASync } from "../PA.Core/Basic/ASync";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { RecordUtils } from "../PA.Core/Basic/Records";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import {
    IBusinessObject,
    IBusinessObjectChanges,
    IBusinessObjectChangesOptions,
    IBusinessObjectCreateLoadOptions
} from "../PA.Core/PA.Core.IF.BO";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { PAdbContext } from "../PA.Core/PAdbContext";
import {
    esWB_VolDecomp,
    esWB_MasterData_Set,
    esWB_SalesArea,
    esWB_SalesOrg,
    esWB_VolDecompPR,
    esWB_Company,
    esWB_Partition,
    esWB_VolDecompPR_BS_Default,
    esWB_VolDecompPR_BS_Detail,
    esWB_VolDecompPR_EDLP_Default,
    esWB_VolDecompPR_EDLP_Detail,
    esWB_VolDecompPR_Merch_Default,
    esWB_Customer,
    esWB_Finished_Product,
    esWB_VolDecompPR_PassThrough_Default,
    esWB_PartitionR,
    esWB_User_Group_Customer,
    esWB_User_Group_SalesArea, esWB_User_Group_Product, esWB_User, esWB_Session, esWB_User_Group, esWB_User_Group_User
} from "../PA.Core/PAdbSchema";
import {
    WB_VolDecompRow,
    WB_MasterDataSetRow,
    WB_SalesAreaRow,
    WB_SalesOrgRow,
    WB_VolDecompPartitionRow,
    WB_CompanyRow,
    WB_PartitionRow,
    WB_VolDecompPRBSDefaultRow,
    WB_VolDecompPRBSDetailRow,
    WB_CustomerRow,
    WB_FinishedProductRow,
    WB_VolDecompPREDLPDetailRow,
    WB_VolDecompPREDLPDefaultRow,
    WB_VolDecompPRMerchDefaultRow, WB_VolDecompPRPassThroughDefaultRow, WB_UserRow
} from "../PA.Core/PAdbSchemaRows";
import { IwbVolDecomp } from "../PA.Core/PAdbSchemaRowsIF";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { DefaultChanges } from "../PA.Core/BusinessObject/BusinessObjectChanges";
import { BehaviorSubject } from "rxjs";
import { PASearch } from "../PA.Core/PASearch";
import { IJobParam } from "../PA.Job.Core/PA.Job.IF.JobParam";
import { JobParamManager } from "../PA.Job.Core/JobParamManager";

type custPrdPair = {
    WB_CUST_ID: number;
    CUSTOMER_NAME: string;
    WB_PROD_ID: number;
    PRODUCT_NAME: string;
};

export class VolDecompBO implements IBusinessObject {
    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_VolDecomp: WB_VolDecompRow;

    _bSalesOrgEnabled: boolean;
    _bSalesAreaEnabled: boolean;
    _ActSpendEnterpriseSelectEnabledChanged: BehaviorSubject<{ SalesOrg: boolean; SalesArea: boolean }>;

    _jobParams: IJobParam[];

    constructor(options?: IBusinessObjectCreateLoadOptions) {
        this.CreateOptions = options || {};
        if ("dbContext" in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({ id: 0, name: "New Control Point" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_VolDecomp = null;

        // enabled after copy, disabled after save
        this._bSalesOrgEnabled = false;
        this._bSalesAreaEnabled = false;
        this._ActSpendEnterpriseSelectEnabledChanged = new BehaviorSubject<{ SalesOrg: boolean; SalesArea: boolean }>(
            undefined
        );
        this._jobParams = [];

        this.IHavePartitions_init();
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">
    get WB_VolDecomp(): WB_VolDecompRow {
        return this.drWB_VolDecomp;
    }

    get bSalesOrgEnabled(): boolean {
        return this._bSalesOrgEnabled;
    }

    get bSalesAreaEnabled(): boolean {
        return this._bSalesAreaEnabled;
    }

    get ActSpendEnterpriseSelectEnabledChanged(): BehaviorSubject<{ SalesOrg: boolean; SalesArea: boolean }> {
        return this._ActSpendEnterpriseSelectEnabledChanged;
    }

    async LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Control Point");
        const olc = (self._OnLoadComplete = (async () => {
            self.LoadOptions = options || {};

            try {
                if ("dbContext" in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = "Unable to determine dbContext for new Vol Decomp Control Point.";
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                const newVolDecompRow = <WB_VolDecompRow>await this.dbContext.createRecordWithKey(esWB_VolDecomp);
                newVolDecompRow.setDefaults();
                self.drWB_VolDecomp = newVolDecompRow;
                self.dbContext.add(esWB_VolDecomp, newVolDecompRow);
                self.Identity.setAll(
                    self.WB_VolDecomp.cif.WB_VOL_DECOMP_ID,
                    self.WB_VolDecomp.cif.VOL_DECOMP_ID,
                    self.WB_VolDecomp.cif.VOL_DECOMP_NAME
                );
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(
                    self.WB_VolDecomp,
                    "WB_VOL_DECOMP_ID",
                    "VOL_DECOMP_ID",
                    "VOL_DECOMP_NAME"
                );

                await self.applyCreateOptions();
                await self.IJobParam_OnLoadComplete();
                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;

                self._OnLoadComplete = undefined;
                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })());
        return olc;
    }

    async Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");
        const olc = (this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = "Customer Type already loaded.";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }
                self.LoadOptions = options || {};

                if ("dbContext" in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = "Unable to determine dbContext for Act Spend.";
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                if ("RecordResolver" in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = VolDecompBO.createRecordResolver({ dbContext: self.dbContext });
                    recordResolverLoadPromise = VolDecompBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise;

                const aVolDecompRow: any = recordResolver.get(esWB_VolDecomp, anId);

                if (_.isEmpty(aVolDecompRow)) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception("Id not found.");
                    return self;
                }

                self.drWB_VolDecomp = aVolDecompRow;

                self.Identity.setAll(
                    self.WB_VolDecomp.cif.WB_VOL_DECOMP_ID,
                    self.WB_VolDecomp.cif.VOL_DECOMP_ID,
                    self.WB_VolDecomp.cif.VOL_DECOMP_NAME
                );
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(
                    self.WB_VolDecomp,
                    "WB_VOL_DECOMP_ID",
                    "VOL_DECOMP_ID",
                    "VOL_DECOMP_NAME"
                );

                await self.IJobParam_OnLoadComplete();

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self;
            } catch (err: unknown) {
                if (err instanceof Error) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                }
                return self;
            }
        })());

        return olc;
    }

    async applyCreateOptions() {
        const self = this;
        const sName = self.CreateOptions?.sName || self.LoadOptions?.sName;
        if (!_.isEmpty(sName)) self.WB_VolDecomp.cif.VOL_DECOMP_NAME = sName;

        // Set Master Data Set
        const wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId;
        if (_.isNumber(wbMasterDataSet)) {
            const mds: WB_MasterDataSetRow = <WB_MasterDataSetRow>(
                await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            );
            if (mds) {
                self.WB_VolDecomp.WB_MasterDataSetRow = mds;
                self.WB_VolDecomp.cif.WB_MDSET_ID = wbMasterDataSet;
            }
        }

        // Set Company
        const wbCompany = self.CreateOptions?.wbCompanyId || self.LoadOptions?.wbCompanyId;
        if (_.isNumber(wbCompany)) {
            const company: WB_CompanyRow = <WB_CompanyRow>await self.dbContext.find(esWB_Company, wbCompany);
            if (company) {
                self.WB_VolDecomp.cif.WB_COMPANY_ID = wbCompany;
            }
        }

        // Set Sales Org
        const wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId;
        if (_.isNumber(wbSalesOrg)) {
            const salesOrg: WB_SalesOrgRow = <WB_SalesOrgRow>await self.dbContext.find(esWB_SalesOrg, wbSalesOrg);
            if (salesOrg) {
                self.WB_VolDecomp.cif.WB_SALESORG_ID = wbSalesOrg;
            }
        }

        // Set Sales Area
        const wbSalesArea = self.CreateOptions?.wbSalesAreaId || self.LoadOptions?.wbSalesAreaId;
        if (_.isNumber(wbSalesArea)) {
            const salesArea: WB_SalesAreaRow = <WB_SalesAreaRow>await self.dbContext.find(esWB_SalesArea, wbSalesArea);
            if (salesArea) {
                self.WB_VolDecomp.cif.WB_SALESAREA_ID = wbSalesArea;
            }
        }
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            "Validating " + self.WB_VolDecomp.cif.CUSTM_NAME + "(" + self.WB_VolDecomp.cif.CUSTM_ID + ")"
        );

        self.ValidateOption = options || {};

        try {
            if (self.dbContext == undefined) {
                const sError = "Unable to determine dbContext to validate Customer Type.";
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            //await VolDecompBOValidations.ValidateFields(self);

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();
        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
        }
        return self;
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: { bSkipValidations?: boolean }): Promise<IBusinessObject> {
        const self = this;
        const wbVolDecomp = <IwbVolDecomp>self.drWB_VolDecomp;
        this.SaveProgressProvider.start(
            "Saving " + wbVolDecomp.VOL_DECOMP_NAME + "(" + wbVolDecomp.VOL_DECOMP_ID + ")"
        );

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = "Unable to determine dbContext to Save Control Point.";
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any
            ) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if (!options?.bSkipValidations) {
                await self.Validate({});

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self;
                }
            }
            await self.SynchronizePartitionOverrideDetailRows();
            await saveFunction(esWB_VolDecomp, self.drWB_VolDecomp, {});
            await saveFunction(esWB_VolDecompPR, self.drWB_VolDecomp.WB_VolDecompPartitionRows, {});
            for (let idx = 0; idx < self.drWB_VolDecomp.WB_VolDecompPartitionRows.length; idx++) {
                const partitionRRow = self.drWB_VolDecomp.WB_VolDecompPartitionRows[idx];

                if (partitionRRow.WB_VolDecompPRBSDefaultRow)
                    await saveFunction(esWB_VolDecompPR_BS_Default, partitionRRow.WB_VolDecompPRBSDefaultRow, {});
                if (partitionRRow.WB_VolDecompPREDLPDefaultRow)
                    await saveFunction(esWB_VolDecompPR_EDLP_Default, partitionRRow.WB_VolDecompPREDLPDefaultRow, {});

                if (partitionRRow.WB_VolDecompPRMerchDefaultRow)
                    await saveFunction(esWB_VolDecompPR_Merch_Default, partitionRRow.WB_VolDecompPRMerchDefaultRow, {});

                if (partitionRRow.WB_VolDecompPRPassThroughDefaultRow)
                    await saveFunction(esWB_VolDecompPR_PassThrough_Default, partitionRRow.WB_VolDecompPRPassThroughDefaultRow, {});

                await saveFunction(esWB_VolDecompPR_BS_Detail, partitionRRow.WB_VolDecompPRBSDetailRows, {});
                await saveFunction(esWB_VolDecompPR_EDLP_Detail, partitionRRow.WB_VolDecompPREDLPDetailRows, {});
            }

            if (
                _.filter(self.WB_VolDecomp.WB_VolDecompPartitionRows, (row) => row.isActive).length + 1 !=
                self.JobParams.length
            ) {
                await self.IJobParam_OnLoadComplete();
            }

            if (self.JobParams.length) {
                for (const jp of self.JobParams) {
                    if (jp.WB_JobParamRow.cif.SOURCE_WB_ID == jp.WB_JobParamRow.cif.WB_ID_TARGET) {
                        jp.id_ui = self.WB_VolDecomp.cif.VOL_DECOMP_ID;
                        jp.name = self.WB_VolDecomp.cif.VOL_DECOMP_NAME;
                    } else {
                        jp.id_ui = jp.WB_JobParamRow.cif.JOB_PARAM_ID;
                        jp.name = jp.WB_JobParamRow.cif.JOB_PARAM_NAME;
                    }

                    await jp.Save(bBatch, { bSkipValidations: true });
                }
            }


            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: { bSkipValidations?: boolean }): Promise<IBusinessObject> {
        const self = this;
        const wbVolDecomp = <IwbVolDecomp>this.drWB_VolDecomp;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " + wbVolDecomp.VOL_DECOMP_NAME + "(" + wbVolDecomp.VOL_DECOMP_ID + ")"
        );

        try {
            self.drWB_VolDecomp.markDeleted();
            _.forEach(self.WB_VolDecomp.WB_VolDecompPartitionRows, (a) => {
                a.markDeleted();
            });

            if (bSaveNow) await self.SaveUsing(false, { bSkipValidations: true });

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            self.DeleteProgressProvider.exception(err);
        }
        return self;
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this;

        const src: VolDecompBO = this;
        const tgt = new VolDecompBO({});
        await tgt.LoadNew({
            wbMasterDataSetId: src.drWB_VolDecomp.cif.WB_MDSET_ID
        });

        if (!tgt.bIsLoaded) {
            throw new Error("Unable to create new Vol Decomp Config");
        }

        tgt.WB_VolDecomp.cif.VOL_DECOMP_NAME = "";
        RecordUtils.CopyProperties(
            tgt.dbContext,
            esWB_VolDecomp,
            src.drWB_VolDecomp,
            tgt.drWB_VolDecomp,
            [],
            [
                "WB_SALESORG_ID",
                "WB_SALESAREA_ID",
                "VOL_DECOMP_ID",
                "VOL_DECOMP_NAME",
                "CREATED_DTTS",
                "MODIFIED_DTTS",
                "DELETED_DT",
                "DELETED_IND"
            ],
            {}
        );
        tgt.Identity.setAll(
            tgt.WB_VolDecomp.cif.WB_VOL_DECOMP_ID,
            tgt.WB_VolDecomp.cif.VOL_DECOMP_ID,
            tgt.WB_VolDecomp.cif.VOL_DECOMP_NAME
        );

        // Copy process definitions
        for (const aSrcPartitionRow of src.WB_VolDecomp.WB_VolDecompPartitionRows) {
            if (!aSrcPartitionRow.isActive) {
                continue;
            }
            const newPartitionRow: WB_VolDecompPartitionRow = await tgt.addPartition(
                aSrcPartitionRow.cif.WB_PARTITION_ID
            );
            newPartitionRow.cif.MANUAL_IND = aSrcPartitionRow.cif.MANUAL_IND;
        }

        tgt._bSalesOrgEnabled = true;
        tgt._bSalesAreaEnabled = true;

        return tgt;
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        const opts = options || {};
        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_VolDecomp, opts);
    }

    //</editor-fold>

    //<editor-fold desc = "Partition and Item Operations">

    private _PartitionsChanged: BehaviorSubject<VolDecompBO>;

    private IHavePartitions_init(): void {
        // Create local BehaviorSubjects
        this._PartitionsChanged = new BehaviorSubject<VolDecompBO>(undefined);
    }

    get PartitionsChanged(): BehaviorSubject<VolDecompBO> {
        return this._PartitionsChanged;
    }

    async addPartition(wbPartitionId?: number): Promise<WB_VolDecompPartitionRow> {
        const self = this;
        //  const bNewPartition: boolean = !_.filter(self.WB_VolDecomp.WB_VolDecompPartitionRows, row => row.isActive && row.cif.WB_PARTITION_ID == wbPartitionId).length

        const cpPartition = <WB_VolDecompPartitionRow>await self.dbContext.createRecordWithKey(esWB_VolDecompPR);
        cpPartition.setDefaults();

        cpPartition.cif.WB_VOL_DECOMP_ID = self.drWB_VolDecomp.cif.WB_VOL_DECOMP_ID;
        if (_.isNumber(wbPartitionId)) {
            cpPartition.cif.WB_PARTITION_ID = wbPartitionId;
            const ptRecordResolver: ESRecordResolver = new ESRecordResolver(self.dbContext,
                {
                    standardConditions: {
                        [esWB_Partition]: { "DELETED_IND": { "notIn": ["1"] } },
                        [esWB_PartitionR]: { "DELETED_IND": { "notIn": ["1"] } }
                    },
                    dctExcludeHasManyNamedRelations: {},
                    dctIncludeBelongsToNamedRelations: {
                        [esWB_Partition]: [esWB_PartitionR]
                    }
                });

            await ptRecordResolver.findAllWithRelationships(esWB_Partition, [wbPartitionId]);
            cpPartition.WB_PartitionRow = <WB_PartitionRow>ptRecordResolver.get(esWB_Partition, wbPartitionId);
        }

        const stages = _.filter(
            self.WB_VolDecomp.WB_VolDecompPartitionRows,
            (aRow) => aRow.isActive && aRow.cif.WB_PARTITION_ID == wbPartitionId
        );
        const maxStage = _.maxBy(stages, (aRow) => aRow.cif.STAGE_ID);
        let maxStageCount = 1;
        if (maxStage) maxStageCount = maxStage.cif.STAGE_ID + 1;

        cpPartition.cif.STAGE_ID = maxStageCount;

        // If partition is added when creating a new control point, the event bindings will happen in the next step. Skip here to avoid repetition
        self.dbContext.add(esWB_VolDecompPR, cpPartition);

        //    if (bNewPartition)
        //        await self.SynchronizePartitionOverrideDetailRows()
        self.PartitionsChanged.next(this);
        return cpPartition;
    }

    async removePartition(wbVolDecompPRId: number): Promise<void> {
        const self = this;

        _.forEach(self.WB_VolDecomp.WB_VolDecompPartitionRows, (aRow) => {
            if (!aRow.isActive) return;

            if (aRow.cif.WB_VOL_DECOMPPR_ID == wbVolDecompPRId) {
                aRow.markDeleted();
            }
        });

        self.PartitionsChanged.next(this);
    }

    async addBSDefault(wbPartitionId: number, wbVolDecompPRId: number): Promise<void> {
        const self = this;

        const partitionRRow = _.first(
            _.filter(
                self.drWB_VolDecomp.WB_VolDecompPartitionRows,
                (aRow) => aRow.isActive && aRow.cif.WB_VOL_DECOMPPR_ID == wbVolDecompPRId
            )
        );

        const checkExisting = _.first(
            _.filter(
                partitionRRow.WB_VolDecompPRBSDefaultRow,
                (aRow: WB_VolDecompPRBSDefaultRow) => aRow.isActive && aRow.cif.WB_VOL_DECOMPPR_ID == wbVolDecompPRId
            )
        );
        if (!_.isUndefined(checkExisting)) return;

        const cpPartitionBSDefault = <WB_VolDecompPRBSDefaultRow>(
            await self.dbContext.createRecordWithKey(esWB_VolDecompPR_BS_Default)
        );
        cpPartitionBSDefault.setDefaults();
        cpPartitionBSDefault.cif.WB_VOL_DECOMPPR_ID = wbVolDecompPRId;
        cpPartitionBSDefault.WB_VolDecompPRRow = partitionRRow

        // If partition is added when creating a new control point, the event bindings will happen in the next step. Skip here to avoid repetition
        self.dbContext.add(esWB_VolDecompPR_BS_Default, cpPartitionBSDefault);
    }

    /** Using the list of customer and product IDs, this method creates any WB_VolDecompPRBSDetailRow records that don't already exist for the given WB_Volume_DecompPR_ID and cust/prd combo
     * and deletes any WB_VolDecompPRBSDetailRow records whose cust/prd combination aren't in the list
     *
     * @param entries The unique, valid Customer/Product combinations that should be in the partition override rows
     */
    async syncBSDetailRecordsToList(
        partitionRRow: WB_VolDecompPartitionRow,
        entries: { WB_CUST_ID: number; WB_PROD_ID: number }[]
    ): Promise<void> {
        /* the override rows should be synced to the customer/product combinations populated in the control's filter
         * if a combination exists in the database that's not in filter, it should be deleted
         * if a combination exists in the filter that is not in the database, a new row should be created
         */

        if (!partitionRRow) {
            return;
        }
        // create a little cache of products that are required to avoid numerous DB calls
        const prdMap: Map<number, WB_FinishedProductRow> = new Map();
        const prdRows = entries.length > 0 ? <WB_FinishedProductRow[]>await this.dbContext.findAll(
            esWB_Finished_Product,
            {
                WB_PROD_ID: { inq: [...new Set(entries.map((e) => e.WB_PROD_ID))] },
                DELETED_IND: { neq: "1" }
            }
        ) : [];
        prdRows.forEach((prd) => prdMap.set(prd.cif.WB_PROD_ID ?? 0, prd));

        // do the same for customers
        const custMap: Map<number, WB_CustomerRow> = new Map();
        const custRows = entries.length > 0 ? <WB_CustomerRow[]>await this.dbContext.findAll(esWB_Customer, {
            WB_CUST_ID: { inq: [...new Set(entries.map((e) => e.WB_CUST_ID))] }
        }) : [];
        custRows.forEach((cust) => custMap.set(cust.cif.WB_CUST_ID ?? 0, cust));

        const existingRecords: Map<string, WB_VolDecompPRBSDetailRow> = new Map();
        partitionRRow.WB_VolDecompPRBSDetailRows.forEach((item) =>
            existingRecords.set(`${item.cif.WB_CUST_ID_PLANNING}-${item.cif.WB_PROD_ID}`, item)
        );
        const newRows: WB_VolDecompPRBSDetailRow[] = [];
        // if a key exists in the parameter, then it's current. if not, it will be deleted below
        const validKeys: Set<string> = new Set();
        for (const entry of entries) {
            const entryKey = `${entry.WB_CUST_ID}-${entry.WB_PROD_ID}`;
            validKeys.add(entryKey);
            if (existingRecords.has(entryKey)) {
                continue;
            }
            const newRow = <WB_VolDecompPRBSDetailRow>this.dbContext.createRecord(esWB_VolDecompPR_BS_Detail, {});
            newRow.setDefaults();
            newRow.cif.WB_CUST_ID = entry.WB_CUST_ID;
            newRow.WB_CustomerRow = custMap.get(entry.WB_CUST_ID);
            newRow.cif.WB_PROD_ID = entry.WB_PROD_ID;
            newRow.WB_ProductRow = prdMap.get(entry.WB_PROD_ID);
            newRow.cif.WB_VOL_DECOMPPR_ID = partitionRRow.cif.WB_VOL_DECOMPPR_ID;
            newRow.WB_VolDecompPRRow = partitionRRow;
            newRow.isActive = true;
            if (partitionRRow.WB_VolDecompPRBSDefaultRow) {
                newRow.setDefaultParameters(partitionRRow.WB_VolDecompPRBSDefaultRow);
            }
            newRows.push(newRow);
        }

        const newKeys: number[] = <number[]>await this.dbContext.getNewIds(esWB_VolDecompPR_BS_Detail, newRows.length);
        if (newKeys.length !== newRows.length) {
            console.error(
                `VolDecompBO: Expected to create ${newRows.length} new base smoothing overrides, created ${newKeys.length}`
            );
            throw new Error("Unable to create create new base smoothing override detail records");
        }
        for (let i: number = 0; i < newKeys.length; i++) {
            newRows[i].cif.WB_VOL_DECOMPPR_BS_DETAIL_ID = newKeys[i];
            this.dbContext.add(esWB_VolDecompPR_BS_Detail, newRows[i]);
        }

        // any rows in the database that aren't in the entries parameter should be deleted
        existingRecords.forEach((value, existingKey) => {
            if (validKeys.has(existingKey)) {
                return;
            }
            value.markDeleted();
            value.isActive = false;
        });
        partitionRRow.WB_VolDecompPRBSDetailRows.push(...newRows);
    }

    async addEDLPDefault(wbPartitionId: number, wbVolDecompPRId: number): Promise<void> {
        const self = this;

        const partitionRRow = _.first(
            _.filter(
                self.drWB_VolDecomp.WB_VolDecompPartitionRows,
                (aRow) => aRow.isActive && aRow.cif.WB_VOL_DECOMPPR_ID == wbVolDecompPRId
            )
        );

        if (!partitionRRow) {
            return;
        }

        const checkExisting = _.first(
            _.filter(
                partitionRRow.WB_VolDecompPREDLPDefaultRow,
                (aRow: WB_VolDecompPREDLPDefaultRow) => aRow.isActive && aRow.cif.WB_VOL_DECOMPPR_ID == wbVolDecompPRId
            )
        );
        if (!_.isUndefined(checkExisting)) return;

        const cpPartitionEDLPDefault = <WB_VolDecompPREDLPDefaultRow>(
            await self.dbContext.createRecordWithKey(esWB_VolDecompPR_EDLP_Default)
        );
        cpPartitionEDLPDefault.setDefaults();
        cpPartitionEDLPDefault.cif.WB_VOL_DECOMPPR_ID = wbVolDecompPRId;
        cpPartitionEDLPDefault.WB_VolDecompPRRow = partitionRRow

        // If partition is added when creating a new control point, the event bindings will happen in the next step. Skip here to avoid repetition
        self.dbContext.add(esWB_VolDecompPR_EDLP_Default, cpPartitionEDLPDefault);
    }

    /** Using the list of customer and product IDs, this method creates any WB_VolDecompPREDLPDetailRow records that don't already exist for the given WB_Volume_DecompPR_ID and cust/prd combo
     * and deletes any WB_VolDecompPREDLPDetailRow records whose cust/prd combination aren't in the list
     *
     * @param entries The unique, valid Customer/Product combinations that should be in the partition override rows
     */
    async syncEDLPDetailRecordsToList(
        partitionRRow: WB_VolDecompPartitionRow,
        entries: { WB_CUST_ID: number; WB_PROD_ID: number }[]
    ): Promise<void> {
        if (!partitionRRow) {
            return;
        }
        // create a little cache of products that are required to avoid numerous DB calls
        const prdMap: Map<number, WB_FinishedProductRow> = new Map();
        const prdRows = entries.length > 0 ? <WB_FinishedProductRow[]>await this.dbContext.findAll(
            esWB_Finished_Product,
            {
                WB_PROD_ID: { inq: [...new Set(entries.map((e) => e.WB_PROD_ID))] },
                DELETED_IND: { neq: "1" }
            }
        ) : [];
        prdRows.forEach((prd) => prdMap.set(prd.cif.WB_PROD_ID, prd));

        // do the same for customers
        const custMap: Map<number, WB_CustomerRow> = new Map();
        const custRows = entries.length > 0 ? <WB_CustomerRow[]>await this.dbContext.findAll(esWB_Customer, {
            WB_CUST_ID: { inq: [...new Set(entries.map((e) => e.WB_CUST_ID))] }
        }) : [];
        custRows.forEach((cust) => custMap.set(cust.cif.WB_CUST_ID, cust));

        const existingRecords: Map<string, WB_VolDecompPREDLPDetailRow> = new Map();
        partitionRRow.WB_VolDecompPREDLPDetailRows.forEach((item) =>
            existingRecords.set(`${item.cif.WB_CUST_ID_PLANNING}-${item.cif.WB_PROD_ID}`, item)
        );
        const newRows: WB_VolDecompPREDLPDetailRow[] = [];
        // if a key exists in the parameter, then it's current. if not, it will be deleted below
        const validKeys: Set<string> = new Set();
        for (const entry of entries) {
            const entryKey = `${entry.WB_CUST_ID}-${entry.WB_PROD_ID}`;
            validKeys.add(entryKey);
            if (existingRecords.has(entryKey)) {
                continue;
            }
            const newRow = <WB_VolDecompPREDLPDetailRow>this.dbContext.createRecord(esWB_VolDecompPR_EDLP_Detail, {});
            newRow.setDefaults();
            newRow.cif.WB_CUST_ID = entry.WB_CUST_ID;
            newRow.WB_CustomerRow = custMap.get(entry.WB_CUST_ID);
            newRow.cif.WB_PROD_ID = entry.WB_PROD_ID;
            newRow.WB_ProductRow = prdMap.get(entry.WB_PROD_ID);
            newRow.cif.WB_VOL_DECOMPPR_ID = partitionRRow.cif.WB_VOL_DECOMPPR_ID;
            newRow.WB_VolDecompPRRow = partitionRRow;
            if (partitionRRow.WB_VolDecompPREDLPDefaultRow) {
                newRow.setDefaultParameters(partitionRRow.WB_VolDecompPREDLPDefaultRow);
            }
            newRows.push(newRow);
        }
        const newKeys: number[] = <number[]>(
            await this.dbContext.getNewIds(esWB_VolDecompPR_EDLP_Detail, newRows.length)
        );
        if (newKeys.length !== newRows.length) {
            console.error(
                `VolDecompBO: Expected to create ${newRows.length} new EDLP overrides, created ${newKeys.length}`
            );
            throw new Error("Unable to create create new EDLP Override detail records");
        }
        for (let i: number = 0; i < newKeys.length; i++) {
            newRows[i].cif.WB_VOL_DECOMPPR_EDLP_DETAIL_ID = newKeys[i];
            this.dbContext.add(esWB_VolDecompPR_EDLP_Detail, newRows[i]);
        }

        // any rows in the database that aren't in the entries parameter should be deleted
        existingRecords.forEach((value, existingKey) => {
            if (validKeys.has(existingKey)) {
                return;
            }
            value.markDeleted();
            value.isActive = false;
        });
        partitionRRow.WB_VolDecompPREDLPDetailRows.push(...newRows);
    }

    private _PartitionCustPrdsCombos: Map<number, custPrdPair[]> = new Map();

    getCustPrdPairForPartition(wbPartitionId: number): custPrdPair[] {
        return [...this._PartitionCustPrdsCombos.get(wbPartitionId)] ?? [];
    }

    /** Synchronizes each type of override row for every active partition in the VolDecompBO.
     * Any missing detail rows will be created, and any extras will be deleted
     */
    async SynchronizePartitionOverrideDetailRows(): Promise<void> {
        const self = this;

        const activePartitionBSStages = _.filter(
            self.drWB_VolDecomp.WB_VolDecompPartitionRows,
            (aRow) => aRow.isActive
        );
        const tasks: Promise<void>[] = [];
        type partitionCustProducts = {
            WB_PARTITION_ID: number;
            WB_CUST_ID: number;
            CUSTOMER_NAME: string;
            WB_PROD_ID: number;
            PRODUCT_NAME: string;
        };

        const getCustPrdPair = (row: partitionCustProducts): custPrdPair => {
            return {
                WB_PROD_ID: row.WB_PROD_ID,
                PRODUCT_NAME: row.PRODUCT_NAME,
                WB_CUST_ID: row.WB_CUST_ID,
                CUSTOMER_NAME: row.CUSTOMER_NAME
            };
        };

        // run one query for all the partitions at once and break the synchronizing work into multiple promises
        const results = <partitionCustProducts[]>await PASearch.find(
            "vUI_Partition_Customer_Products",
            {
                WB_PARTITION_ID: { inq: [...activePartitionBSStages.map((item) => item.cif.WB_PARTITION_ID)] }
            },
            { WB_PARTITION_ID: { type: "number" } },
            [],
            0,
            []
        );

        const byPartition: Map<number, custPrdPair[]> = new Map();
        results.forEach((item) => {
            if (!byPartition.has(item.WB_PARTITION_ID)) {
                byPartition.set(item.WB_PARTITION_ID, []);
            }
            byPartition.get(item.WB_PARTITION_ID).push(getCustPrdPair(item));
        });

        for (const partitionRRow of activePartitionBSStages) {
            const wbPartitionId = partitionRRow.cif.WB_PARTITION_ID;

            // get a list of the unique customer/product combinations
            const custPrds: custPrdPair[] = byPartition.get(wbPartitionId) ?? [];
            this._PartitionCustPrdsCombos.set(wbPartitionId, custPrds);

            switch (partitionRRow.ProcessType) {
                case "BS":
                    tasks.push(this.syncBSDetailRecordsToList(partitionRRow, custPrds));
                    break;
                case "EDLP":
                    tasks.push(this.syncEDLPDetailRecordsToList(partitionRRow, custPrds));
                    break;
            }
        }

        await Promise.all(tasks);
    }

    async addMerchDefault(wbPartitionId: number, wbVolDecompPRId: number): Promise<void> {
        const self = this;

        const partitionRRow = _.first(
            _.filter(
                self.drWB_VolDecomp.WB_VolDecompPartitionRows,
                (aRow) => aRow.isActive && aRow.cif.WB_VOL_DECOMPPR_ID == wbVolDecompPRId
            )
        );

        if (!partitionRRow) {
            return;
        }

        const checkExisting = _.first(
            _.filter(
                partitionRRow.WB_VolDecompPRMerchDefaultRow,
                (aRow: WB_VolDecompPRMerchDefaultRow) =>
                    aRow.isActive && aRow.cif.WB_VOL_DECOMPPR_ID == wbVolDecompPRId
            )
        );
        if (!_.isUndefined(checkExisting)) return;

        const cpPartitionMerchDefault: WB_VolDecompPRMerchDefaultRow = <WB_VolDecompPRMerchDefaultRow>(
            await self.dbContext.createRecordWithKey(esWB_VolDecompPR_Merch_Default)
        );
        cpPartitionMerchDefault.setDefaults();
        cpPartitionMerchDefault.cif.WB_VOL_DECOMPPR_ID = wbVolDecompPRId;
        cpPartitionMerchDefault.WB_VolDecompPRRow = partitionRRow

        // If partition is added when creating a new control point, the event bindings will happen in the next step. Skip here to avoid repetition
        self.dbContext.add(esWB_VolDecompPR_Merch_Default, cpPartitionMerchDefault);
    }

    async addPassThroughDefault(wbPartitionId: number, wbVolDecompPRId: number): Promise<void> {
        const self = this;

        const partitionRRow = _.first(
            _.filter(
                self.drWB_VolDecomp.WB_VolDecompPartitionRows,
                (aRow) => aRow.isActive && aRow.cif.WB_VOL_DECOMPPR_ID == wbVolDecompPRId
            )
        );

        if (!partitionRRow) {
            return;
        }

        const checkExisting = _.first(
            _.filter(
                partitionRRow.WB_VolDecompPRPassThroughDefaultRow,
                (aRow: WB_VolDecompPRMerchDefaultRow) =>
                    aRow.isActive && aRow.cif.WB_VOL_DECOMPPR_ID == wbVolDecompPRId
            )
        );
        if (!_.isUndefined(checkExisting)) return;

        const cpPartitionPassThroughDefault: WB_VolDecompPRPassThroughDefaultRow = <WB_VolDecompPRPassThroughDefaultRow>(
            await self.dbContext.createRecordWithKey(esWB_VolDecompPR_PassThrough_Default)
        );
        cpPartitionPassThroughDefault.setDefaults();
        cpPartitionPassThroughDefault.cif.WB_VOL_DECOMPPR_ID = wbVolDecompPRId;
        cpPartitionPassThroughDefault.WB_VolDecompPRRow = partitionRRow

        // If partition is added when creating a new control point, the event bindings will happen in the next step. Skip here to avoid repetition
        self.dbContext.add(esWB_VolDecompPR_PassThrough_Default, cpPartitionPassThroughDefault);
    }

    //</editor-fold>

    //<editor-fold desc = "staticIRecordResolver">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const createOptions: CreateOptions = {
            standardConditions: {
                [esWB_VolDecomp]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_VolDecompPR]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_VolDecompPR_BS_Default]: { DELETED_IND: { neq: "1" } },
                [esWB_VolDecompPR_EDLP_Default]: { DELETED_IND: { neq: "1" } },
                [esWB_VolDecompPR_BS_Detail]: { DELETED_IND: { neq: "1" } },
                [esWB_VolDecompPR_EDLP_Detail]: { DELETED_IND: { neq: "1" } }
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_VolDecompPR]: [esWB_Partition],
                [esWB_Partition]: [esWB_PartitionR]
            }
        };

        return new ESRecordResolver(dbContext, createOptions);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver
                .findAllWithRelationships(esWB_VolDecomp, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    //</editor-fold>

    //<editor-fold desc = "Job Param">
    async IJobParam_OnLoadComplete(): Promise<void> {
        const self = this;
        const jpMgr = new JobParamManager();
        const sourceWbId = this.WB_VolDecomp.cif.WB_VOL_DECOMP_ID;
        const jobParamId = this.WB_VolDecomp.cif.VOL_DECOMP_ID;
        const jobParamName = this.WB_VolDecomp.cif.VOL_DECOMP_NAME;
        const wbMasterDataSetId = this.WB_VolDecomp.cif.WB_MDSET_ID;
        const wbSalesOrgId = this.WB_VolDecomp.cif.WB_SALESORG_ID;
        const wbCompanyId = this.WB_VolDecomp.cif.WB_COMPANY_ID;

        const jobProcessorType = "VolDecompProcessor";
        let appExecId = "VolDecomp";
        let sourceType = "VolDecomp";
        let dependentTriggerId = "VolDecomp";

        if (
            !_.filter(
                self._jobParams,
                (param) => param.WB_JobParamRow.cif.SOURCE_WB_ID == self.WB_VolDecomp.cif.WB_VOL_DECOMP_ID
            ).length
        ) {
            const jobParam = await jpMgr.findOrCreate({
                dbContext: this.dbContext,
                JobSchedulerModuleId: appExecId,

                sourceType: sourceType,
                sourceWbId: sourceWbId,

                appExecId: appExecId,
                wbTargetId: sourceWbId,

                wbMasterDataSetId: wbMasterDataSetId,
                wbCompanyId: wbCompanyId,
                wbSalesOrgId: wbSalesOrgId,

                JobParamId: jobParamId,
                JobParamName: jobParamName,
                JobProcessorType: jobProcessorType,
                DependentTriggerId: dependentTriggerId
            });

            this._jobParams.push(jobParam);
        }

        for (const pr of _.filter(self.WB_VolDecomp.WB_VolDecompPartitionRows, (row) => row.isActive)) {
            if (
                _.filter(
                    self._jobParams,
                    (param) =>
                        !_.isEmpty(param.WB_JobParamRow.cif.JOB_OPTIONS) &&
                        param.WB_JobParamRow.cif.SOURCE_WB_ID == pr.cif.WB_VOL_DECOMPPR_ID &&
                        param.WB_JobParamRow.cif.WB_ID_TARGET == pr.cif.WB_VOL_DECOMP_ID
                ).length > 0
            )
                continue;

            dependentTriggerId = pr.cif.PROCESS_ID
            switch (pr.cif.PROCESS_ID) {
                case "VOLUME_DECOMP_SMOOTHING":
                    sourceType = "CPBaseSmoothingParameter";
                    appExecId = "VolDecomp";

                    break;
                case "VOLUME_DECOMP_BASE_EDLP_SPLIT":
                    sourceType = "CPEDLPIncrementalParameter";
                    appExecId = "VolDecomp";

                    break;
                case "VOLUME_DECOMP_ROLLING":
                    sourceType = "CPMerchSmoothingParameter";
                    appExecId = "VolDecomp";

                    break;
                case "VOLUME_DECOMP_DISPLAY":
                    sourceType = "CPDisplayIncrementalParameter";
                    appExecId = "VolDecomp";

                    break;

                case "VOLUME_DECOMP_PASSTHROUGH":
                    sourceType = "CPPassThroughParameter";
                    appExecId = "VolDecomp";

                    break;
            }

            const jobParam = await jpMgr.findOrCreate({
                dbContext: this.dbContext,
                JobSchedulerModuleId: appExecId,

                sourceType: sourceType,
                sourceWbId: pr.cif.WB_VOL_DECOMPPR_ID,

                appExecId: appExecId,
                wbTargetId: pr.cif.WB_VOL_DECOMP_ID,

                jobOptions:
                    '{"WB_PARTITION_ID":' +
                    pr.cif.WB_PARTITION_ID.toString() +
                    ',"STAGE_ID":' +
                    pr.cif.STAGE_ID.toString() +
                    "}",

                wbMasterDataSetId: wbMasterDataSetId,
                wbCompanyId: wbCompanyId,
                wbSalesOrgId: wbSalesOrgId,

                JobParamId:
                    self.WB_VolDecomp.cif.VOL_DECOMP_ID +
                    "-" +
                    pr.WB_PartitionRow.cif.PARTITION_ID +
                    "-" +
                    pr.cif.PROCESS_ID +
                    "-" +
                    pr.cif.STAGE_ID.toString(),
                JobParamName:
                    self.WB_VolDecomp.cif.VOL_DECOMP_NAME +
                    "-" +
                    pr.WB_PartitionRow.cif.PARTITION_NAME +
                    "-" +
                    pr.cif.PROCESS_ID +
                    "-" +
                    pr.cif.STAGE_ID.toString(),
                JobProcessorType: jobProcessorType,
                DependentTriggerId: dependentTriggerId
            });

            this._jobParams.push(jobParam);
        }
    }

    get JobParams(): IJobParam[] {
        return this._jobParams;
    }
}

PAEnvironment.registerBusinessObjectClass(VolDecompBO, "VolDecompBO", "Volume Decomp");
